import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApolloError, gql, useLazyQuery, useMutation } from "@apollo/client";
import useFetchPinnedLeadFields from "src/query-hooks/useFetchPinnedLeadFields";
import { LeadCardParentQueue, NextIntent, OptionItem } from "src/types";
import { defaultLeadCardConfig } from "src/utils/misc";
import { CallContext } from "./CallContext";
import { LeadFilterContext } from "./LeadFilterContext";

import { useFlags } from "launchdarkly-react-client-sdk";
import { cloneDeep, isUndefined } from "lodash";
import { ACTIVE_QUEUE_TAKE, PINNED_QUEUE_TAKE, QUEUE_LIMIT } from "src/utils/variables";
import { appToast, errorToast } from "src/utils/toast";
import { IntentType } from "src/__generated__/graphql";

export interface ContextNextIntent extends NextIntent {
  [key: string]: any;
}

interface LeadCardContextState {
  customFieldsExpanded: boolean;
  setCustomFieldsExpanded: Dispatch<SetStateAction<boolean>>;
  historyExpanded: boolean;
  setHistoryExpanded: Dispatch<SetStateAction<boolean>>;
  callNotesExpanded: boolean;
  setCallNotesExpanded: Dispatch<SetStateAction<boolean>>;
  selectedTab: string;
  setSelectedTab: Dispatch<SetStateAction<string>>;
  leadHistoryFilter: string;
  setLeadHistoryFilter: Dispatch<SetStateAction<string>>;
  selectedTabDialQueue: LeadCardParentQueue;
  setSelectedTabDialQueue: Dispatch<SetStateAction<LeadCardParentQueue>>;
  sortOptionDialQueue: string;
  setSortOptionDialQueue: Dispatch<SetStateAction<string>>;
  leadCardFields: OptionItem[];
  setLeadCardFields: Dispatch<SetStateAction<OptionItem[]>>;
  loadingLeadCardFields: boolean;
  upcomingDials: any[];
  setUpcomingDials: Dispatch<SetStateAction<any[]>>;
  customDials: any[];
  setCustomDials: Dispatch<SetStateAction<any[]>>;
  pinnedDials: any[];
  setPinnedDials: Dispatch<SetStateAction<any[]>>;
  favoriteDials: any[];
  setFavoriteDials: Dispatch<SetStateAction<any[]>>;
  refetchPinnedLeads: any;
  leadCardHistoryReset: boolean;
  setLeadCardHistoryReset: Dispatch<SetStateAction<boolean>>;
  ableToOverideNextDial: boolean;
  setAbleToOverideNextDial: Dispatch<SetStateAction<boolean>>;
  leadInNextDial: string;
  setLeadInNextDial: Dispatch<SetStateAction<string>>;
  skip: number;
  setSkip: Dispatch<SetStateAction<number>>;
  activeQueueSkipRef: React.MutableRefObject<number>;
  activeQueueSkip: number;
  customQueueSkipRef: React.MutableRefObject<number>;
  customQueueSkip: number;
  customSkip: number;
  setCustomSkip: Dispatch<SetStateAction<number>>;
  handleResetQueueToDefaultSettings: () => void;
  hasMore: boolean;
  hasMoreCustom: boolean;
  hasMorePinned: boolean;
  activeQueueHasMore: boolean;
  setActiveQueueHasMore: Dispatch<SetStateAction<boolean>>;
  setHasMoreCustom: Dispatch<SetStateAction<boolean>>;
  setHasMorePinned: Dispatch<SetStateAction<boolean>>;
  handleShowMore: () => void;
  handleShowMoreCustom: () => void;
  handleShowMorePinned: () => void;
  pinnedQueueSkip: number;
  setPinnedSkip: Dispatch<SetStateAction<number>>;
  pinnedQueueSkipRef: React.MutableRefObject<number>;
  resetPinnedQueue: boolean;
  setResetPinnedQueue: Dispatch<SetStateAction<boolean>>;

  clearAndRefetchCustomQueue: () => void;
  refreshQueues: () => void;
  errorRecentsV2: any;

  errorActiveQueue: ApolloError | undefined;
  loadingQueue: boolean;

  errorCustom: any;
  loadingRecentsV2: boolean;

  leadIntentInNextDial: NextIntent | undefined;
  setLeadIntentInNextDial: Dispatch<SetStateAction<NextIntent | undefined>>;

  fetchFilteredLeadsInQueueV2: ({
    variables,
  }: {
    variables: { skip: number; take: number; lead_filter: any; sort_option: string };
  }) => void;
  fetchCustomLeadsInQueue: ({
    variables,
  }: {
    variables: {
      skip: number;
      take: number;
      lead_filter: any;
      sort_option: string;
      includeVisibleCustomFields?: boolean;
    };
  }) => void;
  fetchActiveQueue: ({
    variables,
  }: {
    variables: { skip: number; take: number; lead_filter: any; sort_option: string };
  }) => void;
  refetchActiveQueue: any;
  refetchCustomLeadsInQueue: any;

  dataRecommendedActionV2: any;
  loadingRecommendedActionV2: any;
  fetchSuggestedActionLeadV2: ({
    variables,
  }: {
    variables: { skip: number; take: number; lead_filter: any; sort_option: string };
  }) => void;
  resetRecentActivities: () => void;
  recentActivitiesRefreshNow: boolean;
  setRecentActivitiesRefreshNow: Dispatch<SetStateAction<boolean>>;
  activitiesCommunicationType: "Phone" | "SMS" | "Email" | "All";
  setActivitiesCommunicationType: Dispatch<SetStateAction<"Phone" | "SMS" | "Email" | "All">>;

  resetActiveQueue: boolean;
  setResetActiveQueue: Dispatch<SetStateAction<boolean>>;
  resetCustomQueue: boolean;
  setResetCustomQueue: Dispatch<SetStateAction<boolean>>;

  favoriteLead: (lead_id: string) => void;
  unfavoriteLead: (lead_id: string) => void;
  loadingFavoriteLead: boolean;
  loadingUnfavoriteLead: boolean;
  errorFavoriteLead: ApolloError | undefined;
  errorUnfavoriteLead: ApolloError | undefined;
  refetchLeadHistoryWithId: (leadId: string) => void;
  fetchPinnedLeads: ({
    variables,
  }: {
    variables: { skip: number; take: number; lead_filter: any; sort_option: string };
  }) => void;
  leadHistoryChangedList: string[];
  setLeadHistoryChangedList: Dispatch<SetStateAction<string[]>>;
}

export const LeadCardContext = createContext<LeadCardContextState>({} as LeadCardContextState);

export const FETCH_FILTERED_LEADS_IN_QUEUE_V2 = gql`
  query fetchFilteredLeadsInQueueV2(
    $skip: Int
    $take: Int
    $lead_filter: LeadFilter
    $sort_option: DialSortOption
    $includeVisibleCustomFields: Boolean = false
  ) {
    fetchFilteredLeadsInQueueV2(skip: $skip, take: $take, lead_filter: $lead_filter, sort_option: $sort_option) {
      id
      lead_id
      type
      lead {
        id
        brand_name
        brand_id
        organization {
          id
          name
          allow_remove_step_action
          allow_snooze_step_action
        }
        sequence_id
        sequence_step_id
        sequence_step {
          actions {
            id
            task
            custom_task_note
            scheduled_event_behavior
          }
        }
        # 'visible_custom_fields' will increase load, we only need to include if the LeadCardTopInfoV2 component requires a custom field
        visible_custom_fields @include(if: $includeVisibleCustomFields) {
          id
          key
          value
          visible
          boo_value
          num_value
          date_value
          type
          lead_id
          options
          computed_value
          list_value
          allow_reps_to_edit
        }
      }
      id
      lead {
        id
        favorited
        brand_name
        brand_id
        primary_phone_number
        current_lead_type
        city
        state
        country
        lead_value {
          value
        }
        timezone_by_state
        lead_source
        industry
        sub_industry
        address
        channel
        business_name
        full_name
        first_name
        last_name
        lead_ownership_status
        rep_id
        computed_mrr
        current_close_date
        last_lead_activity {
          id
          type
          created_at
        }
      }
      is_scheduled_item
    }
  }
`;

const FETCH_FAVORITED_LEADS = gql`
  query FetchPinnedLeads($lead_filter: LeadFilter!, $skip: Int!, $take: Int!, $sort_option: DialSortOption) {
    fetchFavoritedLeadsV2(lead_filter: $lead_filter, skip: $skip, take: $take, sort_option: $sort_option) {
      # user_id
      # lead_id
      lead {
        id
        favorited
        brand_name
        brand_id
        primary_phone_number
        primary_email
        current_lead_type
        city
        state
        country
        rep_id
        current_close_date
        computed_mrr
        lead_value {
          id
          value
        }
        timezone_by_state
        lead_source
        industry
        sub_industry
        business_name
        full_name
        first_name
        last_name
        lead_ownership_status
        next_lead_intent {
          id
          event_type_label
          dial_num_in_sales_cycle
          anytime_day
          current_sequence_step
          schedule_item {
            id
            start_time
            end_time
            deleted_at
          }
          replacement_lead_intent_id
        }
        next_intent_scheduled_or_unscheduled {
          id
          event_type_label
          dial_num_in_sales_cycle
          anytime_before
          anytime_after
          anytime_day
          anytime_day_upperbound
          anytime_tz
          general_time_start_date
          general_time_end_date
          current_sequence_step
          schedule_item {
            id
            start_time
            end_time
            deleted_at
          }
          replacement_lead_intent_id
          type
          is_scheduled_item
        }
        next_scheduled_event {
          id
          type
          timezone
        }
        next_scheduled_event_time
        pipeline_state_label
        timezone
        visible_custom_fields {
          id
          key
          value
          visible
          boo_value
          num_value
          date_value
          type
          lead_id
          options
          computed_value
          list_value
          allow_reps_to_edit
        }
      }
    }
  }
`;

const FAVORITE_LEAD = gql`
  mutation favoriteLead($lead_id: String!) {
    favoriteLead(lead_id: $lead_id) {
      id
      favorited
    }
  }
`;

const UNFAVORITE_LEAD = gql`
  mutation unfavoriteLead($lead_id: String!) {
    unfavoriteLead(lead_id: $lead_id) {
      id
      favorited
    }
  }
`;
export const FETCH_FILTERED_CUSTOM_QUEUE_V2 = gql`
  query fetchCustomQueueLeads(
    $skip: Int
    $take: Int
    $lead_filter: LeadFilter
    $sort_option: DialSortOption
    $includeVisibleCustomFields: Boolean = false
  ) {
    fetchCustomQueueLeads(lead_filter: $lead_filter, skip: $skip, take: $take, sort_option: $sort_option) {
      user_id
      lead_id
      lead {
        id
        favorited
        brand_name
        brand_id
        primary_phone_number
        primary_email
        current_lead_type
        city
        state
        country
        rep_id
        current_close_date
        computed_mrr
        lead_value {
          id
          value
        }
        timezone_by_state
        lead_source
        industry
        sub_industry
        business_name
        full_name
        first_name
        last_name
        lead_ownership_status
        next_lead_intent {
          id
          event_type_label
          dial_num_in_sales_cycle
          anytime_day
          current_sequence_step
          schedule_item {
            id
            start_time
            end_time
            deleted_at
          }
          replacement_lead_intent_id
        }
        next_intent_scheduled_or_unscheduled {
          id
          event_type_label
          dial_num_in_sales_cycle
          anytime_before #general time
          anytime_after
          anytime_day
          anytime_day_upperbound
          anytime_tz
          general_time_start_date
          general_time_end_date
          current_sequence_step
          schedule_item {
            #specific time
            id
            start_time
            end_time
            deleted_at
          }
          replacement_lead_intent_id
          type
          is_scheduled_item
        }
        next_scheduled_event {
          id
          type
          timezone
        }
        next_scheduled_event_time
        pipeline_state_label
        timezone
        # 'visible_custom_fields' will increase load, we only need to include if the LeadCardTopInfoV2 component requires a custom field
        visible_custom_fields @include(if: $includeVisibleCustomFields) {
          id
          key
          value
          visible
          boo_value
          num_value
          date_value
          type
          lead_id
          options
          computed_value
          list_value
          allow_reps_to_edit
        }
      }
    }
  }
`;

const FETCH_RECOMMENDED_ACTION_V2 = gql`
  query fetchFilteredLeadsInQueueV2($skip: Int, $take: Int, $lead_filter: LeadFilter, $sort_option: DialSortOption) {
    fetchFilteredLeadsInQueueV2(skip: $skip, take: $take, lead_filter: $lead_filter, sort_option: $sort_option) {
      id
      lead_id
      type
      lead {
        id
        brand_name
        brand_id
        sequence_id
        sequence_step_id
        sequence_step {
          actions {
            id
            task
            custom_task_note
            scheduled_event_behavior
          }
        }
        organization {
          id
          name
          allow_remove_step_action
          allow_snooze_step_action
        }
        favorited
        primary_phone_number
        primary_email
        current_lead_type
        city
        state
        country
        timezone_by_state
        lead_source
        industry
        sub_industry
        business_name
        full_name
        first_name
        last_name
        lead_ownership_status
        rep_id
        computed_mrr
        current_close_date
      }
      is_scheduled_item
    }
  }
`;

export const FETCH_ACTIVE_QUEUE = gql`
  query fetchActiveQueue($skip: Int, $take: Int, $lead_filter: LeadFilter, $sort_option: DialSortOption) {
    fetchFilteredLeadsInQueueV2(skip: $skip, take: $take, lead_filter: $lead_filter, sort_option: $sort_option) {
      id
      type
      is_scheduled_item
      lead {
        id
        favorited
        primary_phone_number
        conference_number
        current_lead_type
        city
        state
        country
        lead_value {
          value
        }
        is_in_active_queue
        timezone_by_state
        lead_source
        industry
        sub_industry
        address
        channel
        business_name
        full_name
        first_name
        last_name
        city
        state
        country
        address
        channel
        rep_id
        timezone_by_state
        lead_source
        industry
        sub_industry
        lead_ownership_status
        current_lead_type
        computed_mrr
        current_close_date

        pipeline_state_label

        zip

        primary_email
        primary_email_title
        primary_email_type

        primary_phone_number
        primary_phone_number_country_code
        local_primary_phone_number
        primary_phone_number_title
        primary_phone_number_type

        alternate_contacts {
          id
          title
          channel
          type
          value
          mapping_order
          contact_label
          country_code
        }

        flagged_numbers: LeadFlaggedNumber {
          id
          phone_number
          lead_activity {
            related_disposition {
              id
              type
            }
          }
        }

        next_intent_scheduled_or_unscheduled {
          id
          type
          replacement_lead_intent_id
          event_type_label
          general_time_start_date
          general_time_end_date
          dial_num_in_sales_cycle
          anytime_day
          current_sequence_step
        }

        next_lead_intent {
          id
          schedule_item {
            id
            deleted_at
          }
        }

        next_scheduled_event {
          id
          type
        }
        next_scheduled_event_time

        sequence_id
        sequence_step_id
        sequence_step {
          actions {
            id
            task
            custom_task_note
            scheduled_event_behavior
          }
        }

        visible_custom_fields {
          id
          key
          value
          visible
          boo_value
          num_value
          date_value
          type
          lead_id
          options
          computed_value
          list_value
          allow_reps_to_edit
        }

        last_lead_activity {
          id
          type
          created_at
        }

        brand {
          id
        }

        organization {
          id
          name
          allow_remove_step_action
          allow_snooze_step_action
        }

        email_sub_status
        sms_sub_status
      }
    }
  }
`;

export const LeadCardProvider: FunctionComponent = ({ children }) => {
  const { superDialer } = useFlags();

  const { nextActionOverride, resetNextDial } = useContext(CallContext);

  const { leadFilter, resetLeadQueueFilters } = useContext(LeadFilterContext);

  const [leadCardFields, setLeadCardFields] = useState<OptionItem[]>(defaultLeadCardConfig);
  const { loading: loadingLeadCardFields, includesCustomFields } = useFetchPinnedLeadFields({ setLeadCardFields });
  //State that holds active/ custom queue data so it can be removed from any lead card as well
  const [upcomingDials, setUpcomingDials] = useState<any[]>([]);
  const [customDials, setCustomDials] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [activeQueueHasMore, setActiveQueueHasMore] = useState(true);
  const [hasMoreCustom, setHasMoreCustom] = useState(true);
  const [hasMorePinned, setHasMorePinned] = useState(true);
  const [favoriteDials, setFavoriteDials] = useState<any[]>([]);
  const [pinnedDials, setPinnedDials] = useState<any[]>([]);
  const [leadIntentInNextDial, setLeadIntentInNextDial] = useState<NextIntent | undefined>(undefined);
  // State that holds whether Custom Fields / Additional Information is expanded
  const [customFieldsExpanded, setCustomFieldsExpanded] = useState(
    Boolean(localStorage.getItem("lead_card_custom_fields_expanded")) || false,
  );
  const [historyExpanded, setHistoryExpanded] = useState(
    Boolean(localStorage.getItem("lead_card_history_expanded")) || false,
  );
  const [callNotesExpanded, setCallNotesExpanded] = useState(
    Boolean(localStorage.getItem("lead_card_call_notes_expanded")) || false,
  );

  const [selectedTab, setSelectedTab] = useState(localStorage.getItem("selected_lead_card_tab") || "contact-info");

  const [leadHistoryFilter, setLeadHistoryFilter] = useState(localStorage.getItem("lead_history_filter") || "All");

  const [selectedTabDialQueue, setSelectedTabDialQueue] = useState<LeadCardParentQueue>(
    (localStorage.getItem("selected_tab_dial_queue") as LeadCardParentQueue) || "active",
  );
  const [sortOptionDialQueue, setSortOptionDialQueue] = useState(
    localStorage.getItem("sort_option_dial_queue") || "OrganizationOrder",
  );

  const [leadCardHistoryReset, setLeadCardHistoryReset] = useState(false);

  const { setSuggestedActionData, useLegacyDialer } = useContext(CallContext);

  // this value lets other cards know if the current next dial lead is a scheduled event (blocks next dial)
  const [ableToOverideNextDial, setAbleToOverideNextDial] = useState<boolean>(
    Boolean(localStorage.getItem("ableToOverideNextDial") === "true"),
  );

  // this value lets other cards check if they are already in the next dial
  const [leadInNextDial, setLeadInNextDial] = useState(localStorage.getItem("leadInNextDial") || "");

  const [skip, setSkip] = useState(0);
  const activeQueueSkipRef = useRef(0);
  const activeQueueSkip = activeQueueSkipRef?.current || 0;
  const customQueueSkipRef = useRef(0);
  const customQueueSkip = customQueueSkipRef?.current || 0;
  const pinnedQueueSkipRef = useRef(0);
  const pinnedQueueSkip = pinnedQueueSkipRef?.current || 0;
  const [resetActiveQueue, setResetActiveQueue] = useState(false);
  const [resetCustomQueue, setResetCustomQueue] = useState(false);
  const [resetPinnedQueue, setResetPinnedQueue] = useState(false);

  const [customSkip, setCustomSkip] = useState(0);
  const [pinnedSkip, setPinnedSkip] = useState(0);

  // Recent Activities
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [recentActivitiesSkip, setRecentActivitiesSkip] = useState(0);
  const [recentActivitiesHasMore, setRecentActivitiesHasMore] = useState(true);
  const [activitiesCommunicationType, setActivitiesCommunicationType] = useState<"Phone" | "SMS" | "Email" | "All">(
    "All",
  );

  // Lead History Changes Table
  const [leadHistoryChangedList, setLeadHistoryChangedList] = useState([] as string[]);

  /**
   * Refetch Lead History Changes table for a specific lead ID
   * @param leadId - ID of Lead to refetch History Changes for
   */
  const refetchLeadHistoryWithId = (leadId: string) => {
    setLeadHistoryChangedList([...leadHistoryChangedList, leadId]);
  };

  const [recentActivitiesRefreshNow, setRecentActivitiesRefreshNow] = useState(false);

  // suggested action card
  const [
    fetchSuggestedActionLeadV2,
    { data: dataRecommendedActionV2, loading: loadingRecommendedActionV2, error: errorRecommendedActionV2 },
  ] = useLazyQuery(FETCH_RECOMMENDED_ACTION_V2, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      const intentData = dataRecommendedActionV2?.fetchFilteredLeadsInQueueV2?.[0];

      const leadData = intentData?.lead;

      setSuggestedActionData({
        intentData: intentData,
        leadData: leadData,
        expectedAction: leadData?.sequence_step?.actions?.[0]?.task,
      });
      console.log("suggestedActionData:", {
        intentData: intentData,
        leadData: leadData,
        expectedAction: leadData?.sequence_step?.actions?.[0]?.task,
      });
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  // active queue
  const [
    fetchFilteredLeadsInQueueV2,
    { data: dataRecentsV2, loading: loadingRecentsV2, error: errorRecentsV2 },
  ] = useLazyQuery(FETCH_FILTERED_LEADS_IN_QUEUE_V2, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      // remove duplicates
      const newList = [...upcomingDials, ...dataRecentsV2?.fetchFilteredLeadsInQueueV2].reduce(
        (acc, cv) => (acc?.map((a: any) => a?.id).includes(cv?.id) ? acc : [...acc, cv]),
        [],
      );

      if (dataRecentsV2?.fetchFilteredLeadsInQueueV2?.length < 4) setHasMore(false);
      else setHasMore(true);

      setUpcomingDials(newList);
    },
  });

  // super active queue
  const [
    fetchActiveQueue,
    { data: dataActiveQueue, loading: loadingActiveQueue, error: errorActiveQueue, refetch: refetchActiveQueue },
  ] = useLazyQuery(FETCH_ACTIVE_QUEUE, {
    fetchPolicy: "no-cache",
    variables: {
      skip: activeQueueSkip,
      take: ACTIVE_QUEUE_TAKE,
      lead_filter: leadFilter,
      sort_option: sortOptionDialQueue,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      const listA = resetActiveQueue ? [] : cloneDeep(upcomingDials);
      const listB = cloneDeep(dataActiveQueue?.fetchFilteredLeadsInQueueV2 ?? []);
      const newList = listA.concat(listB);

      if (
        dataActiveQueue?.fetchFilteredLeadsInQueueV2?.length < 4 ||
        upcomingDials.length + dataActiveQueue?.fetchFilteredLeadsInQueueV2?.length >= QUEUE_LIMIT
      )
        setActiveQueueHasMore(false);
      else setActiveQueueHasMore(true);
      setUpcomingDials(newList);

      // resetActiveQueue is true on logDisposition or when we get an inbound from ably to refresh queue
      resetActiveQueue && setResetActiveQueue(false);
    },
  });
  const [
    fetchCustomLeadsInQueue,
    { data: dataCustom, loading: loadingCustom, error: errorCustom, refetch: refetchCustomLeadsInQueue },
  ] = useLazyQuery(FETCH_FILTERED_CUSTOM_QUEUE_V2, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      // remove duplicates
      const newList = [...(!resetCustomQueue ? customDials : []), ...(dataCustom?.fetchCustomQueueLeads || [])].reduce(
        (acc, cv) => (acc?.map((a: any) => a?.lead_id).includes(cv?.lead_id) ? acc : [...acc, cv]),
        [],
      );

      // set hasMore before setting the list to prevent doubled requests
      if (data?.fetchCustomQueueLeads?.length < 4) setHasMoreCustom(false);
      else setHasMoreCustom(true);

      // set the list
      setCustomDials(newList);

      // resetCustomQueue is true on logDisposition or when we get an inbound from ably to refresh queue
      resetCustomQueue && setResetCustomQueue(false);
    },
  });

  const [
    fetchPinnedLeads,
    {
      data: dataFavorites,
      loading: loadingFavorites,
      error: errorFavorites,
      fetchMore: fetchMoreFavorites,
      refetch: refetchPinnedLeads,
    },
  ] = useLazyQuery(FETCH_FAVORITED_LEADS, {
    fetchPolicy: "no-cache",
    variables: {
      skip: pinnedQueueSkip,
      take: PINNED_QUEUE_TAKE,
      lead_filter: leadFilter,
      sort_option: sortOptionDialQueue,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      const listA = resetPinnedQueue ? [] : cloneDeep(pinnedDials);
      const listB = cloneDeep(dataFavorites?.fetchFavoritedLeadsV2 ?? []);

      const newList = listA.concat(listB);

      if (
        dataFavorites?.fetchFavoritedLeadsV2?.length < 4 ||
        pinnedDials.length + dataFavorites?.fetchFavoritedLeadsV2?.length >= QUEUE_LIMIT
      )
        setHasMorePinned(false);
      else setHasMorePinned(true);
      setPinnedDials(newList);

      // resetPinnedQueue is true on logDisposition or when we get an inbound from ably to refresh queue
      resetPinnedQueue && setResetPinnedQueue(false);
    },
  });

  const loadingQueue = useMemo(() => loadingActiveQueue || loadingCustom || loadingFavorites, [
    loadingActiveQueue,
    loadingCustom,
    loadingFavorites,
  ]);

  const resetRecentActivities = () => {
    setRecentActivitiesRefreshNow(true);
  };

  const clearAndRefetchCustomQueue = () => {
    setCustomDials([]);
    fetchCustomLeadsInQueue({
      variables: {
        skip: 0,
        take: 4,
        lead_filter: leadFilter,
        sort_option: sortOptionDialQueue,
      },
    });
  };

  const handleShowMore = () => {
    // edge case failsafe where this is triggered before the first fetch is complete
    if (!dataRecentsV2) return;

    fetchFilteredLeadsInQueueV2({
      variables: {
        skip: skip + 4,
        take: 4,
        lead_filter: leadFilter,
        sort_option: sortOptionDialQueue,
        includeVisibleCustomFields: !!includesCustomFields || isUndefined(includesCustomFields),
      },
    });

    // set skip for the next fetch
    setSkip(skip + 4);
  };

  //Custom Functions
  const handleShowMoreCustom = () => {
    fetchCustomLeadsInQueue({
      variables: {
        skip: customSkip + 4,
        take: 4,
        lead_filter: leadFilter,
        sort_option: sortOptionDialQueue,
      },
    });
    // set skip for the next fetch
    setCustomSkip(customSkip + 4);
  };

  const handleShowMorePinned = () => {
    if (!dataFavorites) return;

    fetchPinnedLeads({
      variables: {
        skip: pinnedQueueSkip + 4,
        take: 4,
        lead_filter: leadFilter,
        sort_option: sortOptionDialQueue,
      },
    });

    // set skip for the next fetch
    setPinnedSkip(pinnedQueueSkip + 4);
  };

  const refreshQueues = () => {
    if (!useLegacyDialer && !!superDialer) {
      if (selectedTabDialQueue === "active") {
        setResetActiveQueue(true);
        fetchActiveQueue({
          variables: {
            skip: 0,
            take: ACTIVE_QUEUE_TAKE,
            lead_filter: leadFilter,
            sort_option: sortOptionDialQueue,
          },
        });
      } else if (selectedTabDialQueue === "custom") {
        fetchCustomLeadsInQueue({
          variables: {
            skip: 0,
            take: ACTIVE_QUEUE_TAKE,
            lead_filter: leadFilter,
            sort_option: sortOptionDialQueue,
            includeVisibleCustomFields: !!includesCustomFields || isUndefined(includesCustomFields),
          },
        });
      } else if (selectedTabDialQueue === "pinned") {
        setResetPinnedQueue(true);
        fetchPinnedLeads({
          variables: {
            skip: 0,
            take: PINNED_QUEUE_TAKE,
            lead_filter: leadFilter,
            sort_option: sortOptionDialQueue,
          },
        });
      }
      return;
    }

    // fetch new data based on selected tab
    if (selectedTabDialQueue === "active") {
      setSkip(0);
      setUpcomingDials([]);

      // sel-11511 hotfix

      const shouldUseLegacyFetch = useLegacyDialer || !superDialer || superDialer === undefined;

      shouldUseLegacyFetch &&
        fetchFilteredLeadsInQueueV2({
          variables: {
            skip: 0,
            take: 4,
            lead_filter: leadFilter,
            sort_option: sortOptionDialQueue,
            includeVisibleCustomFields: !!includesCustomFields || isUndefined(includesCustomFields),
          },
        });
    } else if (selectedTabDialQueue === "custom") {
      setCustomSkip(0);
      setCustomDials([]);
      fetchCustomLeadsInQueue({
        variables: {
          skip: 0,
          take: 4,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
          includeVisibleCustomFields: !!includesCustomFields || isUndefined(includesCustomFields),
        },
      });
    }
  };

  const handleResetQueueToDefaultSettings = () => {
    // reset sort
    setSortOptionDialQueue("OrganizationOrder");

    // reset filters
    resetLeadQueueFilters();
  };

  useEffect(() => {
    localStorage.setItem("lead_card_custom_fields_expanded", customFieldsExpanded ? "true" : "");
  }, [customFieldsExpanded]);

  useEffect(() => {
    localStorage.setItem("lead_card_history_expanded", historyExpanded ? "true" : "");
  }, [historyExpanded]);

  useEffect(() => {
    localStorage.setItem("lead_card_call_notes_expanded", callNotesExpanded ? "true" : "");
  }, [callNotesExpanded]);

  useEffect(() => {
    localStorage.setItem("selected_lead_card_tab", !!selectedTab ? selectedTab : "contact-info");
  }, [selectedTab]);

  useEffect(() => {
    localStorage.setItem("lead_history_filter", !!leadHistoryFilter ? leadHistoryFilter : "All");
  }, [leadHistoryFilter]);

  useEffect(() => {
    localStorage.setItem("selected_tab_dial_queue", !!selectedTabDialQueue ? selectedTabDialQueue : "active");
  }, [selectedTabDialQueue]);

  useEffect(() => {
    localStorage.setItem("sort_option_dial_queue", !!sortOptionDialQueue ? sortOptionDialQueue : "HighestPDV");
  }, [sortOptionDialQueue]);

  useEffect(() => {
    localStorage.setItem("ableToOverideNextDial", !!ableToOverideNextDial ? "true" : "false");
  }, [ableToOverideNextDial]);

  useEffect(() => {
    localStorage.setItem("leadInNextDial", !!leadInNextDial ? leadInNextDial : "");
  }, [leadInNextDial]);

  const handleFavoriteLead = (lead_id: string) => {
    // optimistic update
    setUpcomingDials((prev) =>
      prev.map((intent) =>
        intent?.lead?.id === lead_id ? { ...intent, lead: { ...intent.lead, favorited: true } } : intent,
      ),
    );

    setCustomDials((prev) =>
      prev.map((intent) =>
        intent?.lead?.id === lead_id ? { ...intent, lead: { ...intent.lead, favorited: true } } : intent,
      ),
    );

    // real mutation
    favoriteLead({ variables: { lead_id: lead_id } });
  };

  const [favoriteLead, { loading: loadingFavoriteLead, error: errorFavoriteLead }] = useMutation(FAVORITE_LEAD, {
    onCompleted: (data) => {
      appToast("Lead favorited successfully");

      // refetch favorited leads is done with refetchQueries
    },
    onError: (error) => {
      errorToast("Error favoriting lead");
      // refetch queues to undo optimistic update
      refetchPinnedLeads?.();
      refetchActiveQueue?.();
      refetchCustomLeadsInQueue?.();

      console.error("Error favoriting lead:", error);
    },
    refetchQueries: [],
  });

  const handleUnfavoriteLead = (lead_id: string) => {
    // optimistic update

    // remove from pinnedDials
    setPinnedDials((prev) => prev.filter((intent) => intent?.lead?.id !== lead_id));

    setUpcomingDials((prev) =>
      prev.map((intent) =>
        intent?.lead?.id === lead_id ? { ...intent, lead: { ...intent.lead, favorited: false } } : intent,
      ),
    );

    setCustomDials((prev) =>
      prev.map((intent) =>
        intent?.lead?.id === lead_id ? { ...intent, lead: { ...intent.lead, favorited: false } } : intent,
      ),
    );

    // real mutation
    unfavoriteLead({ variables: { lead_id: lead_id } });
  };

  const [unfavoriteLead, { loading: loadingUnfavoriteLead, error: errorUnfavoriteLead }] = useMutation(
    UNFAVORITE_LEAD,
    {
      onCompleted: (data) => {
        appToast("Lead unfavorited successfully");
        // remove from pinnedDials
        setPinnedDials((prev) => prev.filter((intent) => intent?.lead?.id !== data?.unfavoriteLead?.lead_id));
      },
      onError: (error) => {
        console.error("Error unfavoriting lead:", error);
        errorToast("Error unfavoriting lead");
        // refetch queues to undo optimistic update
        refetchPinnedLeads?.();
        refetchActiveQueue?.();
        refetchCustomLeadsInQueue?.();
      },
      refetchQueries: [],
    },
  );

  const memoizedValue = useMemo(
    () => ({
      customFieldsExpanded,
      setCustomFieldsExpanded,
      historyExpanded,
      setHistoryExpanded,
      callNotesExpanded,
      setCallNotesExpanded,
      selectedTab,
      setSelectedTab,
      leadHistoryFilter,
      setLeadHistoryFilter,
      selectedTabDialQueue,
      setSelectedTabDialQueue,
      sortOptionDialQueue,
      setSortOptionDialQueue,
      leadCardFields,
      setLeadCardFields,
      loadingLeadCardFields,
      upcomingDials,
      setUpcomingDials,
      customDials,
      setCustomDials,
      favoriteDials,
      setFavoriteDials,
      pinnedDials,
      setPinnedDials,
      leadCardHistoryReset,
      setLeadCardHistoryReset,
      ableToOverideNextDial,
      setAbleToOverideNextDial,
      leadInNextDial,
      setLeadInNextDial,
      skip,
      setSkip,
      customSkip,
      setCustomSkip,
      activeQueueSkip,
      activeQueueSkipRef,
      customQueueSkip,
      customQueueSkipRef,
      pinnedQueueSkip,
      setPinnedSkip,
      pinnedQueueSkipRef,
      resetPinnedQueue,
      setResetPinnedQueue,
      handleResetQueueToDefaultSettings,
      hasMore,
      hasMoreCustom,
      hasMorePinned,
      activeQueueHasMore,
      setActiveQueueHasMore,
      setHasMoreCustom,
      setHasMorePinned,
      handleShowMore,
      handleShowMoreCustom,
      handleShowMorePinned,
      refreshQueues,
      errorRecentsV2,
      errorActiveQueue,
      loadingQueue,
      errorCustom,
      clearAndRefetchCustomQueue,
      dataRecommendedActionV2,
      loadingRecommendedActionV2,
      fetchSuggestedActionLeadV2,
      fetchFilteredLeadsInQueueV2,
      fetchCustomLeadsInQueue,
      fetchPinnedLeads,
      fetchActiveQueue,
      refetchActiveQueue,
      refetchCustomLeadsInQueue,
      refetchPinnedLeads,
      resetActiveQueue,
      setResetActiveQueue,
      resetCustomQueue,
      setResetCustomQueue,
      loadingRecentsV2,
      leadIntentInNextDial,
      setLeadIntentInNextDial,
      recentActivities,
      setRecentActivities,
      recentActivitiesSkip,
      setRecentActivitiesSkip,
      recentActivitiesHasMore,
      setRecentActivitiesHasMore,
      resetRecentActivities,
      recentActivitiesRefreshNow,
      setRecentActivitiesRefreshNow,
      activitiesCommunicationType,
      setActivitiesCommunicationType,
      refetchLeadHistoryWithId,
      leadHistoryChangedList,
      setLeadHistoryChangedList,
      favoriteLead: handleFavoriteLead,
      unfavoriteLead: handleUnfavoriteLead,
      loadingFavoriteLead,
      loadingUnfavoriteLead,
      errorFavoriteLead,

      errorUnfavoriteLead,
    }),
    [
      customFieldsExpanded,
      setCustomFieldsExpanded,
      historyExpanded,
      setHistoryExpanded,
      callNotesExpanded,
      setCallNotesExpanded,
      selectedTab,
      setSelectedTab,
      leadHistoryFilter,
      setLeadHistoryFilter,
      selectedTabDialQueue,
      setSelectedTabDialQueue,
      sortOptionDialQueue,
      setSortOptionDialQueue,
      leadCardFields,
      setLeadCardFields,
      loadingLeadCardFields,
      upcomingDials,
      setUpcomingDials,
      customDials,
      setCustomDials,
      favoriteDials,
      setFavoriteDials,
      pinnedDials,
      setPinnedDials,
      setFavoriteDials,
      leadCardHistoryReset,
      setLeadCardHistoryReset,
      ableToOverideNextDial,
      setAbleToOverideNextDial,
      leadInNextDial,
      setLeadInNextDial,
      skip,
      setSkip,
      activeQueueSkip,
      activeQueueSkipRef,
      customQueueSkip,
      customQueueSkipRef,
      customSkip,
      setCustomSkip,
      pinnedQueueSkip,
      setPinnedSkip,
      pinnedQueueSkipRef,
      resetPinnedQueue,
      setResetPinnedQueue,
      handleResetQueueToDefaultSettings,
      hasMore,
      hasMoreCustom,
      hasMorePinned,
      activeQueueHasMore,
      setActiveQueueHasMore,
      setHasMoreCustom,
      setHasMorePinned,
      handleShowMore,
      handleShowMoreCustom,
      handleShowMorePinned,
      refreshQueues,
      errorRecentsV2,
      errorActiveQueue,
      loadingQueue,
      errorCustom,
      clearAndRefetchCustomQueue,
      dataRecommendedActionV2,
      loadingRecommendedActionV2,
      fetchSuggestedActionLeadV2,
      fetchFilteredLeadsInQueueV2,
      fetchCustomLeadsInQueue,
      fetchPinnedLeads,
      fetchActiveQueue,
      refetchActiveQueue,
      refetchCustomLeadsInQueue,
      refetchPinnedLeads,
      resetActiveQueue,
      setResetActiveQueue,
      resetCustomQueue,
      setResetCustomQueue,
      loadingRecentsV2,
      leadIntentInNextDial,
      setLeadIntentInNextDial,
      recentActivities,
      setRecentActivities,
      recentActivitiesSkip,
      setRecentActivitiesSkip,
      recentActivitiesHasMore,
      setRecentActivitiesHasMore,
      resetRecentActivities,
      recentActivitiesRefreshNow,
      setRecentActivitiesRefreshNow,
      activitiesCommunicationType,
      setActivitiesCommunicationType,
      leadHistoryChangedList,
      favoriteLead,
      unfavoriteLead,
      loadingFavoriteLead,
      loadingUnfavoriteLead,
      errorFavoriteLead,
      errorUnfavoriteLead,
    ],
  );

  return <LeadCardContext.Provider value={memoizedValue}>{children}</LeadCardContext.Provider>;
};
